/**
 * Sawyer E4 (#754) — "Tag a dog": pick the dog a board post is about.
 *
 * Lists the dogs at the facility today (a staffer with pets:read:all also
 * gets a name match across every dog once they type two letters; the server
 * decides, AC6). One overlay level, a search field, a listbox; arrows +
 * Enter pick, Escape closes.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { staffMessagesApi, type DogTag } from '../../lib/staff-messages-api.js';

type State = { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; dogs: DogTag[] };

export function DogTagPicker({
  apiUrl,
  onPick,
  onClose,
}: {
  apiUrl: string;
  onPick: (dog: DogTag) => void;
  onClose: () => void;
}): JSX.Element {
  const [q, setQ] = useState('');
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const handle = setTimeout(() => {
      void staffMessagesApi.dogs(apiUrl, q.trim()).then((r) => {
        if (cancelled) return;
        setState(r.ok ? { kind: 'ready', dogs: r.data.dogs } : { kind: 'error' });
        setActive(0);
      });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [apiUrl, q]);

  const dogs = state.kind === 'ready' ? state.dogs : [];

  function onKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, Math.max(dogs.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const dog = dogs[active];
      if (dog) onPick(dog);
    }
  }

  const term = q.trim();
  return (
    <div className="ferro-tm-overlay" onKeyDown={onKeyDown}>
      <div className="ferro-tm-overlay__backdrop" onClick={onClose} aria-hidden="true" />
      <div className="ferro-tm-dialog" role="dialog" aria-modal="true" aria-labelledby="tm-dog-title" data-testid="dog-tag-picker">
        <div className="ferro-tm-dialog__head">
          <h2 id="tm-dog-title" className="ferro-tm-dialog__title">
            Tag a dog
          </h2>
          <button type="button" className="ferro-tm-dialog__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <label className="ferro-tm__sr" htmlFor="tm-dog-search">
          Search dogs here today
        </label>
        <input
          id="tm-dog-search"
          ref={inputRef}
          className="ferro-tm-dialog__search"
          type="search"
          placeholder="Search dogs here today"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-controls="tm-dog-list"
          autoComplete="off"
          data-testid="dog-tag-search"
        />
        {state.kind === 'loading' ? (
          <p className="ferro-tm-dialog__note">Looking…</p>
        ) : state.kind === 'error' ? (
          <p className="ferro-tm-dialog__note" role="alert">
            Couldn&apos;t load the dogs. Check your connection and try again.
          </p>
        ) : dogs.length === 0 ? (
          <p className="ferro-tm-dialog__note" data-testid="dog-tag-empty">
            {term ? `No dogs here today match '${term}'.` : 'No dogs are booked here today.'}
          </p>
        ) : (
          <ul id="tm-dog-list" className="ferro-tm-dialog__list" aria-label="Dogs">
            {dogs.map((d, i) => (
              <li key={d.id}>
                <button
                  type="button"
                  id={`tm-dog-${d.id}`}
                  className={`ferro-tm-dialog__option${i === active ? ' ferro-tm-dialog__option--active' : ''}`}
                  aria-current={i === active ? 'true' : undefined}
                  onClick={() => onPick(d)}
                  onMouseEnter={() => setActive(i)}
                  data-testid="dog-tag-option"
                >
                  <span className="ferro-tm-dialog__option-name">{d.name}</span>
                  {d.breed ? <span className="ferro-tm-dialog__option-meta"> · {d.breed}</span> : null}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
